import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import Alert from '../components/Alert';
import '../styles/login.css';

const Login = () => {
  // 1 = email+clave, 2 = empresa, 3 = sede
  const [step, setStep] = useState(1);
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [alert,   setAlert]       = useState(null);

  // Paso 1 → guarda info devuelta
  const [preauth,  setPreauth]    = useState(null); // { access_token, empresas, usuario, nombre, ... }

  // Paso 2
  const [idEmpresa, setIdEmpresa] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [sinRestriccion, setSinRestriccion] = useState(false);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  // Paso 3
  const [idSucursal, setIdSucursal] = useState('');

  const { finalizarSesion, setSucursal, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (isAuthenticated) navigate('/dashboard'); }, [isAuthenticated, navigate]);

  // ─── Paso 1: email + password ───
  const handlePaso1 = async (e) => {
    e.preventDefault();
    if (!email || !password) { setAlert({ type: 'warning', message: 'Ingrese email y contraseña.' }); return; }
    setLoading(true); setAlert(null);
    try {
      const res = await authService.loginCredenciales({ email, password });
      setPreauth(res.data);
      // Si tiene una sola empresa, saltamos directo al paso 3
      if (res.data.empresas.length === 1) {
        const solaEmpresa = res.data.empresas[0];
        setIdEmpresa(String(solaEmpresa.id_empresa));
        await cargarSucursales(solaEmpresa.id_empresa, res.data.access_token);
      } else {
        setStep(2);
      }
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Credenciales inválidas.' });
    } finally { setLoading(false); }
  };

  // ─── Paso 2: seleccionar empresa ───
  const cargarSucursales = async (idEmp, preauthToken) => {
    setLoadingSucursales(true); setAlert(null);
    try {
      const res = await authService.seleccionarEmpresa(
        { id_empresa: parseInt(idEmp, 10) },
        preauthToken || preauth.access_token,
      );
      setSucursales(res.data.sucursales || []);
      setSinRestriccion(!!res.data.sin_restriccion);
      setIdSucursal('');
      setStep(3);
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'No tiene acceso a esa empresa.' });
    } finally { setLoadingSucursales(false); }
  };

  const handlePaso2 = async (e) => {
    e.preventDefault();
    if (!idEmpresa) { setAlert({ type: 'warning', message: 'Seleccione una empresa.' }); return; }
    await cargarSucursales(idEmpresa);
  };

  // ─── Paso 3: seleccionar sede y finalizar ───
  const handleFinalizar = async (e) => {
    e.preventDefault();
    if (sucursales.length > 0 && !idSucursal) {
      setAlert({ type: 'warning', message: 'Seleccione una sede.' }); return;
    }
    setLoading(true); setAlert(null);
    try {
      const res = await authService.finalizarLogin(
        { id_empresa: parseInt(idEmpresa, 10), id_sucursal: idSucursal ? parseInt(idSucursal, 10) : null },
        preauth.access_token,
      );
      const tokenData = res.data;
      finalizarSesion(tokenData);
      if (tokenData.sucursal) setSucursal(tokenData.sucursal);
      navigate('/dashboard');
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'No se pudo finalizar el ingreso.' });
    } finally { setLoading(false); }
  };

  const empresaSeleccionada = preauth?.empresas?.find(e => String(e.id_empresa) === String(idEmpresa));

  const volver = (toStep) => { setAlert(null); setStep(toStep); };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
          <h1>Siscorp Almacen</h1>
          <p>
            {step === 1 && 'Ingrese sus credenciales'}
            {step === 2 && 'Seleccione la empresa'}
            {step === 3 && 'Seleccione la sede'}
          </p>
        </div>

        {/* Breadcrumb de pasos */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, margin: '0 0 22px' }}>
          {[1, 2, 3].map(n => (
            <React.Fragment key={n}>
              <div style={{
                width: 26, height: 26, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: step >= n ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.05)',
                color:      step >= n ? '#93c5fd' : '#475569',
                border: `1.5px solid ${step >= n ? '#60a5fa' : 'rgba(255,255,255,0.08)'}`,
              }}>{n}</div>
              {n < 3 && <div style={{ width: 24, height: 2, background: step > n ? '#60a5fa' : 'rgba(255,255,255,0.08)' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* PASO 1 — Credenciales */}
        {step === 1 && (
          <form onSubmit={handlePaso1}>
            <div className="form-group">
              <label>Correo electrónico</label>
              <input type="email" placeholder="correo@empresa.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" autoFocus required />
            </div>
            <div className="form-group">
              <label>Contraseña</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ingrese su contraseña"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password" required />
                <button type="button" className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                  {showPassword ? '◉' : '○'}
                </button>
              </div>
            </div>
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? 'Verificando...' : 'Continuar'}
            </button>
          </form>
        )}

        {/* PASO 2 — Empresa */}
        {step === 2 && preauth && (
          <form onSubmit={handlePaso2}>
            <div style={{
              background: 'rgba(96,165,250,0.08)', borderRadius: 8,
              padding: '10px 14px', marginBottom: 18, fontSize: 12.5, color: '#93c5fd',
              border: '1px solid rgba(96,165,250,0.2)',
            }}>
              ● Sesión iniciada como <strong>{preauth.nombre}</strong>
            </div>
            <div className="form-group">
              <label>Empresa</label>
              <select value={idEmpresa} onChange={(e) => setIdEmpresa(e.target.value)} autoFocus required>
                <option value="">— Seleccione una empresa —</option>
                {preauth.empresas.map(emp => (
                  <option key={emp.id_empresa} value={emp.id_empresa}>
                    {emp.razon_social}{emp.ruc ? ` — ${emp.ruc}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="login-btn" disabled={loadingSucursales}>
              {loadingSucursales ? 'Cargando sedes...' : 'Continuar'}
            </button>
            <button type="button" className="login-btn" onClick={() => volver(1)}
              style={{ background: 'transparent', border: '1px solid #334', color: '#8899aa', marginTop: 8 }}>
              ← Volver
            </button>
          </form>
        )}

        {/* PASO 3 — Sede */}
        {step === 3 && preauth && (
          <form onSubmit={handleFinalizar}>
            <div style={{
              background: 'rgba(96,165,250,0.08)', borderRadius: 8,
              padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: '#93c5fd',
              border: '1px solid rgba(96,165,250,0.2)', lineHeight: 1.6,
            }}>
              ● Usuario: <strong>{preauth.nombre}</strong><br />
              ● Empresa: <strong>{empresaSeleccionada?.razon_social}</strong>
            </div>
            <div className="form-group">
              <label>Sede de trabajo</label>
              {sucursales.length === 0 ? (
                <div style={{
                  padding: '10px 12px', borderRadius: 8, fontSize: 12,
                  color: '#f59e0b', background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.25)',
                }}>
                  ⚠ No hay sedes permitidas para este usuario en la empresa.
                </div>
              ) : (
                <>
                  <select value={idSucursal} onChange={(e) => setIdSucursal(e.target.value)} required>
                    <option value="">— Seleccione una sede —</option>
                    {sucursales.map(s => (
                      <option key={s.id_sucursal} value={s.id_sucursal}>
                        {s.codigo} — {s.nombre}
                      </option>
                    ))}
                  </select>
                  {sinRestriccion && (
                    <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 4 }}>
                      El usuario tiene acceso a todas las sedes de esta empresa.
                    </div>
                  )}
                </>
              )}
            </div>
            <button type="submit" className="login-btn" disabled={loading || sucursales.length === 0}>
              {loading ? 'Ingresando...' : 'Ingresar al sistema'}
            </button>
            <button type="button" className="login-btn"
              onClick={() => volver(preauth.empresas.length > 1 ? 2 : 1)}
              style={{ background: 'transparent', border: '1px solid #334', color: '#8899aa', marginTop: 8 }}>
              ← Volver
            </button>
          </form>
        )}
      </div>

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
    </div>
  );
};

export default Login;
